import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { Folder } from "types/qbank/folder";
import FolderIcon from "@mui/icons-material/Folder";
import { useFilter } from "hooks/filter";
import {
  ClickAwayListener,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useApi } from "hooks/api";
import { useQuery } from "react-query";

type FolderListItemProps = {
  open: boolean;
  folder: Folder;
  anchorEl: null | HTMLElement;
  handleClose: (event: Event | React.SyntheticEvent) => void;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
};

const FolderList = ({
  open,
  folder,
  anchorEl,
  handleClose,
  setAnchorEl,
}: FolderListItemProps) => {
  const [xFolder, setXFolder] = useState(folder);
  const { searchDispatch } = useFilter();

  const { getFolder } = useApi();
  const {
    isLoading,
    isError,
    data: currentFolder,
  } = useQuery(["getFolder", xFolder.id], () => getFolder(xFolder.id), {
    retry: 0,
  });

  useEffect(() => {
    setXFolder(folder);
  }, [folder]);

  const changeFolder = (newFolder: Folder) => {
    setXFolder(newFolder);
    setAnchorEl(null);
    searchDispatch({
      type: "CHANGE_FOLDER",
      payload: newFolder,
    });
  };

  if (isLoading || isError || !currentFolder) {
    return null;
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      role={undefined}
      placement="bottom-start"
      transition
      disablePortal
      style={{ zIndex: 4000 }}
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === "bottom-start" ? "left top" : "left bottom",
          }}
        >
          <Paper elevation={2}>
            <ClickAwayListener onClickAway={handleClose}>
              <List
                sx={{
                  width: "100%",
                  maxWidth: 300,
                  bgcolor: "background.paper",
                }}
              >
                {currentFolder.parents && currentFolder.parents.length > 0 && (
                  <ListItemButton
                    role={undefined}
                    onClick={(e) => {
                      setXFolder(
                        // @ts-ignore
                        currentFolder.parents?.find((p) => {
                          return p.depth === 1;
                        }).folder
                      );
                    }}
                    dense
                  >
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <ArrowBackIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Back" />
                  </ListItemButton>
                )}
                {currentFolder.subFolders.map((subFolder: Folder) => {
                  const hasSubFolder = subFolder.subFolders.length > 0;
                  const restProps = {};
                  if (hasSubFolder) {
                    // @ts-ignore
                    restProps.secondaryAction = (
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setXFolder(subFolder);
                        }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    );
                  }
                  return (
                    <ListItem key={subFolder.id} {...restProps} disablePadding>
                      <ListItemButton
                        role={undefined}
                        onClick={() => changeFolder(subFolder)}
                        dense
                      >
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <FolderIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={subFolder.name} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

export default FolderList;
